import { DailyAudio, useDaily, useDailyEvent } from '@daily-co/daily-react';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useAppSelector, usePlayCallAudio } from '../../hooks';
import { MeetingState, PracticeProspect } from '../../types';
import { generateCallSid, generateRingingDuration, generateStreamSid } from '../../utils';
import DialerContent from './DialerContent';
import { NextProspectDetails } from './DialerEndActions';
import DialerWrapper from './DialerWrapper';

const PROSPECT_HUNG_UP_ERROR_MSG = 'Meeting has ended';

interface DailyDialerProps {
  currProspect: PracticeProspect;
  callNextProspect: () => void;
  closeDialer: () => void;
  setProspects: Dispatch<SetStateAction<PracticeProspect[]>>;
  nextProspectDetails?: NextProspectDetails;
}

const DailyDialer = ({
  currProspect,
  callNextProspect,
  closeDialer,
  setProspects,
  nextProspectDetails,
}: DailyDialerProps) => {
  const daily = useDaily();

  const [callSid, setCallSid] = useState('');
  const [isMicAccessGranted, setIsMicAccessGranted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [meetingState, setMeetingState] = useState(MeetingState.IDLE);
  // Used to store the timeout ID for the ringing audio which allows us to
  // stop the ringing audio if the user ends the call before the call connects.
  const [ringingTimoutId, setRingingTimoutId] = useState<NodeJS.Timeout | null>(null);

  const userId = useAppSelector((state) => state.auth.user?.id);

  const { playErrorAudio, pauseErrorAudio, playHangUpAudio, playRingingAudio, pauseRingingAudio } = usePlayCallAudio();

  const personaId = currProspect.personaId;

  const handleError = useCallback(async () => {
    // Pause the ringing audio if it is playing.
    pauseRingingAudio();

    // Update the meeting state to error and play the error audio.
    setMeetingState(MeetingState.ERROR);
    await playErrorAudio();

    // After the error audio ends, update the meeting state to hang-up and play the hang-up audio.
    setMeetingState(MeetingState.HANGING_UP);
    await playHangUpAudio();

    // After the hang-up audio ends, update the meeting state to idle.
    setMeetingState(MeetingState.IDLE);
  }, [playErrorAudio, playHangUpAudio, pauseRingingAudio]);

  // Function to ping the API to start a Daily call.
  const createRoom = useCallback(
    async (newCallSid: string) => {
      console.log('🔄 Creating Daily call room...');
      const streamSid = generateStreamSid();
      const url = `${process.env.REACT_APP_WEBSOCKET_API_URL}/daily/start-call?call_sid=${newCallSid}&stream_sid=${streamSid}&user_id=${userId}&prospect_id=${personaId}`;
      try {
        const response = await fetch(url, { method: 'GET' });
        if (response.ok) {
          console.log('✅ Successfully created call room');
          return true;
        } else {
          console.error('❌ Failed to create call room: ', response.statusText);
          return false;
        }
      } catch (error) {
        console.error('❌ Failed to create call room: ', error);
        return false;
      }
    },
    [userId, personaId]
  );

  // Function to start the Daily call and join it.
  const startCall = useCallback(async () => {
    if (!daily || !isMicAccessGranted) return;

    setMeetingState(MeetingState.JOINING_MEETING);
    setIsMuted(false);

    // First start the Daily call via API.
    const newCallSid = generateCallSid();

    // Record the start time of the call and start playing the ringing audio.
    playRingingAudio();

    const startTime = Date.now();
    const started = await createRoom(newCallSid);

    if (!started) {
      // If the call wasn't not created successfully,
      // handle the error by pausing the ringing audio and resetting the meeting state.
      handleError();
      return;
    }

    setCallSid(newCallSid);

    // Generate a random ringing duration and check if it has elapsed.
    // If not, continue ringing for the remaining duration.
    const ringingDuration = generateRingingDuration();
    const durationSoFar = Date.now() - startTime;
    const remainingRingingDuration = ringingDuration > durationSoFar ? ringingDuration - durationSoFar : 0;

    const ringingTimoutId = setTimeout(() => {
      daily.join({
        url: `${process.env.REACT_APP_DAILY_URL}/${newCallSid}`,
        inputSettings: {
          audio: {
            processor: { type: 'noise-cancellation' },
            settings: {
              noiseSuppression: { ideal: true },
              echoCancellation: { ideal: true },
              autoGainControl: { ideal: true },
            },
          },
        },
      });
    }, remainingRingingDuration);

    // Store the ringingTimoutId so we can clear it in case the user stops the call before the call actually starts.
    setRingingTimoutId(ringingTimoutId);
  }, [daily, isMicAccessGranted, createRoom, playRingingAudio, handleError]);

  // Leave the call.
  const stopCall = useCallback(async () => {
    if (!daily || meetingState === MeetingState.IDLE) return;

    // In case of stopping the call while the error audio is playing,
    // pause the error audio, play the hang-up sound, and update the meeting state to idle.
    if (meetingState === MeetingState.ERROR) {
      pauseErrorAudio();
      setMeetingState(MeetingState.HANGING_UP);
      await playHangUpAudio();
      setMeetingState(MeetingState.IDLE);
      return;
    }

    if (meetingState === MeetingState.JOINING_MEETING || ringingTimoutId) {
      // If the call is ringing, that means the participant has not joined the call yet,
      // so stopping the call will just require clearing the timeout and resetting the meeting state.
      console.log('📞 Cancelled Daily call before connection');

      // Clear the callSid so that call operations cannot be performed.
      setCallSid('');
      pauseRingingAudio();
      setMeetingState(MeetingState.IDLE);

      if (ringingTimoutId) {
        clearTimeout(ringingTimoutId);
        setRingingTimoutId(null);
      }
    }

    // Ensure the call is properly terminated by explicitly leaving the Daily.co meeting.
    await daily.leave();
  }, [daily, meetingState, ringingTimoutId, pauseErrorAudio, pauseRingingAudio, playRingingAudio]);

  // Toggle audio mute state
  const toggleMute = useCallback(() => {
    if (!daily) return;

    setIsMuted((prev) => {
      const newIsMuted = !prev;
      daily.setLocalAudio(!newIsMuted); // Invert the mute state to set local audio.
      return newIsMuted;
    });
  }, [daily]);

  // Call loading event.
  // First triggered event in the sequence of events following join().
  useDailyEvent('loading', () => {
    console.log('🔄 Connecting to Daily call...');
    setRingingTimoutId(null);
  });

  // Call connected event.
  useDailyEvent('joined-meeting', async () => {
    console.log('✅ Successfully connected to Daily call');

    setMeetingState(MeetingState.JOINED_MEETING);
    pauseRingingAudio();
    // Initial audio state check.
    if (daily) {
      setIsMuted(!daily.localAudio());
    }

    toggleMute();

    // Unmute after 1 second
    setTimeout(() => {
      toggleMute();
    }, 1000);
  });

  // Call disconnected event.
  useDailyEvent('left-meeting', async () => {
    console.log('📞 Disconnected from Daily call');

    // If meeting state is idle, that probably means that
    // the error event was triggered before the left-meeting event,
    // so we do not need to handle hanging up again.
    if (meetingState === MeetingState.IDLE) return;

    setMeetingState(MeetingState.HANGING_UP);
    await playHangUpAudio();
    setMeetingState(MeetingState.IDLE);
  });

  // Unrecoverable call error event.
  // If a call is in progress when the error is thrown, a left-meeting event will be triggered straight after.
  useDailyEvent('error', async (event) => {
    // If the prospect hung up the call, do not show an error.
    if (event.errorMsg === PROSPECT_HUNG_UP_ERROR_MSG) return;

    console.error('❌ Daily call error: ', event.errorMsg);

    // Clear the callSid so that call operations cannot be performed.
    setCallSid('');
    handleError();
  });

  return (
    <DialerWrapper
      callSid={callSid}
      currProspect={currProspect}
      isMicAccessGranted={isMicAccessGranted}
      meetingState={meetingState}
      handleClose={closeDialer}
      runStartCall={startCall}
      setIsMicAccessGranted={setIsMicAccessGranted}
      setProspects={setProspects}
      stopCall={stopCall}
    >
      <DailyAudio />
      <DialerContent
        callSid={callSid}
        currProspect={currProspect}
        isDaily
        meetingState={meetingState}
        nextProspectDetails={nextProspectDetails}
        callNextProspect={callNextProspect}
        closeDialer={closeDialer}
        redial={startCall}
        setProspects={setProspects}
        stopCall={stopCall}
        isMuted={isMuted}
        toggleMute={toggleMute}
      />
    </DialerWrapper>
  );
};

export default DailyDialer;
